import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Client, Interaction } from "../../models";
import Header from "../Header";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ReactMarkdown from "react-markdown";

import InteractionTable from "../Interactions/InteractionTable";
import AddInteractionDialog from "../Interactions/AddInteractionDialog";
import EditClientDialog from "./EditClientDialog";
import { httpsCallable } from "firebase/functions"; // Import for Firebase callable functions
import { functions } from "../../firebase"; // Import Firebase functions instance

const ClientDetails: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [clientSummary, setClientSummary] = useState<string | null>(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchClient = async () => {
      if (!user || !clientId) return;

      try {
        const clientDocRef = doc(db, "clients", clientId);
        const clientDocSnap = await getDoc(clientDocRef);

        if (clientDocSnap.exists()) {
          setClient({
            id: clientDocSnap.id,
            ...clientDocSnap.data(),
          } as Client);
        } else {
          setError("Client not found");
        }
      } catch (error) {
        console.error("Error fetching client:", error);
        setError("Failed to fetch client. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
    fetchInteractions();
  }, [user, clientId]);

  const fetchInteractions = async () => {
    if (!user || !clientId) return;

    try {
      const interactionsQuery = query(
        collection(db, "interactions"),
        where("clientId", "==", clientId)
      );
      const interactionsSnapshot = await getDocs(interactionsQuery);
      const interactionsData: Interaction[] = interactionsSnapshot.docs.map(
        (doc) => ({
          id: doc.id,
          ...doc.data(),
        })
      ) as Interaction[];
      setInteractions(interactionsData);
    } catch (error) {
      console.error("Error fetching interactions:", error);
      setError("Failed to fetch interactions. Please try again.");
    }
  };

  const handleInteractionClick = (interaction: Interaction) => {
    navigate(`/interactions/${interaction.id}`);
  };

  const handleUpdateClient = async () => {
    if (!user || !client) {
      setEditDialogOpen(false);
      return;
    }

    try {
      const docRef = doc(db, "clients", client.id);
      await updateDoc(docRef, {
        firstName: client.firstName,
        lastName: client.lastName,
        phoneNumber: client.phoneNumber,
        email: client.email,
        company: client.company,
      });
      setError(null);
    } catch (error) {
      console.error("Error updating client:", error);
      setError("Failed to update client. Please try again.");
    } finally {
      setEditDialogOpen(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!user || !client) return;

    try {
      const docRef = doc(db, "clients", client.id);
      await deleteDoc(docRef);
      navigate("/"); // Redirect to home page after deletion
    } catch (error) {
      console.error("Error deleting client:", error);
      setError("Failed to delete client. Please try again.");
    }
  };

  const handleGenerateClientSummary = async () => {
    setGeneratingSummary(true);
    try {
      const clientSummaries = interactions
        .filter((interaction) => interaction.meetingSummary)
        .map((interaction) => ({
          title: interaction.title,
          date: interaction.date,
          summary: interaction.meetingSummary,
        }));

      if (clientSummaries.length === 0) {
        setError("No meeting summaries available to generate client summary");
        return;
      }

      const extractClientSummary = httpsCallable(
        functions,
        "extractClientSummary"
      );
      const result = await extractClientSummary({ clientSummaries });
      setClientSummary((result.data as { summary: string }).summary);
      setError(null);
    } catch (error) {
      console.error("Error generating client summary:", error);
      setError("Failed to generate client summary. Please try again.");
    } finally {
      setGeneratingSummary(false);
    }
  };

  if (loading) {
    return <div>Loading client details...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!client) {
    return <div>No client data available</div>;
  }

  return (
    <div className="min-h-screen bg-gradient text-white">
      <Header />
      <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(-1)}
          sx={{ mb: 3 }}
        >
          Back
        </Button>
        <Grid container spacing={4}>
          {/* Left Column: Client Details */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ padding: 3 }}>
              <Typography variant="h4" gutterBottom>
                Client Details
              </Typography>
              <Typography variant="body1">
                <strong>Name:</strong> {client.firstName} {client.lastName}
              </Typography>
              <Typography variant="body1">
                <strong>Email:</strong> {client.email}
              </Typography>
              <Typography variant="body1">
                <strong>Phone:</strong> {client.phoneNumber}
              </Typography>
              <Typography variant="body1">
                <strong>Company:</strong> {client.company}
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setEditDialogOpen(true)}
                >
                  Edit Client
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleDeleteClient}
                  sx={{ ml: 2 }}
                >
                  Delete Client
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Client Summary</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateClientSummary}
                  disabled={
                    interactions.filter((i) => i.meetingSummary).length === 0 ||
                    generatingSummary
                  }
                >
                  {generatingSummary
                    ? "Generating..."
                    : "Generate Client Summary"}
                </Button>
              </Box>
              {clientSummary ? (
                <Box
                  sx={{
                    "& p": {
                      mb: 2,
                      lineHeight: 1.6,
                    },
                  }}
                >
                  <ReactMarkdown>{clientSummary}</ReactMarkdown>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {interactions.filter((i) => i.meetingSummary).length === 0
                    ? "No meeting summaries available. Add interaction summaries to generate a client summary."
                    : "Click the button above to generate a summary of all client interactions."}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Right Column: Interactions */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ padding: 3 }}>
              <Typography variant="h5" gutterBottom>
                Interactions
              </Typography>
              <InteractionTable
                interactions={interactions}
                onInteractionClick={handleInteractionClick}
              />
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setAddDialogOpen(true)}
                >
                  Add Interaction
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Edit Client Dialog */}
        <EditClientDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          client={{ ...client }}
          onSave={handleUpdateClient}
        />
        {/* Add Interaction Dialog */}
        <AddInteractionDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          clientId={clientId!}
          onInteractionAdded={() => {
            setAddDialogOpen(false);
            // Refresh interactions
            fetchInteractions();
          }}
        />
      </Box>
    </div>
  );
};

export default ClientDetails;
